export interface DefaultableBookingOffering {
  service_name?: string | null;
  default_preferred?: boolean;
  /**
   * Staff booking flows opt into a deterministic fallback when there is no
   * remembered choice. Family booking deliberately leaves multiple
   * undifferentiated choices unselected instead of silently picking one.
   */
  automatic_default_fallback?: boolean;
}

const THIRTY_MINUTE_SERVICE = /\b30\s*(?:minute|min(?:ute)?s?)\b/i;

/**
 * Pick a default only from choices the caller has already decided are valid.
 * A single remembered successful-booking preference wins. A sole valid choice
 * is safe to select in every flow. Staff flows may explicitly opt into the
 * 30-minute/first-choice fallback; family flows keep the ambiguity guard.
 */
export function defaultBookingOffering<T extends DefaultableBookingOffering>(
  offerings: readonly T[],
): T | null {
  if (offerings.length === 0) return null;

  const preferred = offerings.filter(
    (offering) => offering.default_preferred === true,
  );
  if (preferred.length === 1) return preferred[0]!;
  if (offerings.length === 1) return offerings[0]!;

  if (!offerings.every((offering) => offering.automatic_default_fallback === true)) {
    return null;
  }

  return (
    offerings.find((offering) =>
      THIRTY_MINUTE_SERVICE.test(offering.service_name ?? ""),
    ) ?? offerings[0]!
  );
}
